"use client";

import { motion } from "motion/react";
import { Calendar, CreditCard, RotateCw, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const features1 = [
  {
    title: "Smart Scheduling",
    desc: "An intuitive calendar that handles complex booking, buffer times, and team member rotations effortlessly.",
    icon: Calendar,
  },
  {
    title: "Seamless Payments",
    desc: "Accept every way your clients want to pay. Flat rate processing with no hidden surprises.",
    icon: CreditCard,
  },
  {
    title: "Automated Marketing",
    desc: "SMS and email campaigns that fill your empty slots and keep clients coming back.",
    icon: RotateCw,
  },
  {
    title: "Team Excellence",
    desc: "Manage payroll, commissions, and individual team member performance with transparent reporting.",
    icon: Users,
  },
];

export function BusinessTypeFeaturesGrid() {
  return (
    <section id="features" className="relative py-16 md:pt-32">
      <div className="max-w-4xl mx-auto text-center  px-4">
        <h3 className="text-3xl md:text-[4rem] font-display tracking-[-0.03em] leading-[1.14] font-medium text-pretty">
          <span className="relative mr-2 md:mr-3">
            Everything
            <span className="from-[#ff9966] vai-[#ff5e62] to-[#ffc53d] absolute -bottom-1.5 start-0 h-1 w-full rounded-full bg-gradient-to-r"></span>
          </span>
          you need to thrive
        </h3>
        <p className="mt-7 text-pretty text-base text-neutral-600 sm:text-lg">
          Source and attract the best-matching talent and grab their attention
          easily. We give you all the tools for hiring success.
        </p>
      </div>
      <div className="max-w-5xl mx-auto mt-24 px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {features1.map((f, idx) => (
            <motion.div
              key={idx}
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.8,
                ease: [0.21, 0.47, 0.32, 0.98],
              }}
            >
              <Card className="py-8 bg-white h-full">
                <CardContent className="flex flex-col gap-6 px-6 sm:px-8">
                  <span className="bg-neutral-800 w-fit p-4 rounded-3xl">
                    <f.icon
                      className="size-7 md:size-8 text-white"
                      strokeWidth={1.5}
                    />
                  </span>
                  <div className="mt-10">
                    <h4 className="text-xl md:text-2xl text-neutral-900">
                      {f.title}
                    </h4>
                    <p className="mt-4 text-base text-neutral-600">{f.desc}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 3500 350"
        fill="none"
        className="absolute top-2/5 -z-10 mb-10 opacity-50"
      >
        <path
          d="M0 130.391C257 130.391 296 77.8914 420 77.8914C555.5 77.8914 632.5 185.891 784.5 185.891C936.5 185.891 999 26.8914 1152 19.3913C1305 11.8913 1395 113.891 1395 179.891C1395 282.891 1239 253.891 1315.5 112.391C1377.67 -2.60844 1527 66.8915 1640 115.392C1753 163.892 1818.5 204.892 1948 122.392C2077.5 39.8916 2142.5 32.6217 2277.5 129.392C2458 258.777 2596.5 75.6318 2778.5 82.8913C2960.5 90.1508 3001.5 204.892 3142 204.892C3277 204.892 3363.5 130.391 3500 130.391"
          stroke="url(#paint0_linear_pricing_wave)"
          strokeWidth={38}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          transform="translate(0 60)"
          style={{
            strokeDashoffset: 0,
            strokeDasharray: 4119.24,
          }}
        />
        <defs>
          <linearGradient
            id="paint0_linear_pricing_wave"
            x1={942.251}
            y1={53.0237}
            x2={2505.2}
            y2={53.0237}
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#ffc53d" />
            <stop offset={0.35} stopColor="#ffc53d" />
            <stop offset={0.65} stopColor="#ff9966" />
            <stop offset={1} stopColor="#ff9966" />
          </linearGradient>
        </defs>
      </svg>
    </section>
  );
}
